import {
  browserSessionPersistence,
  setPersistence,
  signInWithCustomToken,
  type ParsedToken,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/lib/firebase';

export interface ImpersonationContext {
  sessionId: string;
  adminUid: string;
  adminEmail: string;
  targetUserId: string;
  expiresAt?: Date;
}

function readStringClaim(claims: ParsedToken, key: string): string | null {
  const value = claims[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function parseImpersonationClaims(
  targetUserId: string,
  claims: ParsedToken
): ImpersonationContext | null {
  if (claims.adminImpersonation !== true) {
    return null;
  }

  const sessionId = readStringClaim(claims, 'impersonationSessionId');
  const adminUid = readStringClaim(claims, 'impersonatedBy');
  if (!sessionId || !adminUid) {
    return null;
  }

  const expiresAtMillis =
    typeof claims.impersonationExpiresAt === 'number'
      ? claims.impersonationExpiresAt
      : undefined;

  return {
    sessionId,
    adminUid,
    adminEmail: readStringClaim(claims, 'impersonatedByEmail') ?? '',
    targetUserId,
    expiresAt: expiresAtMillis ? new Date(expiresAtMillis) : undefined,
  };
}

export async function signInWithImpersonationToken(
  customToken: string
): Promise<ImpersonationContext | null> {
  await setPersistence(auth, browserSessionPersistence);
  const credential = await signInWithCustomToken(auth, customToken);
  const idToken = await credential.user.getIdTokenResult(true);
  return parseImpersonationClaims(credential.user.uid, idToken.claims);
}

export async function endImpersonationSession(sessionId: string): Promise<void> {
  const callable = httpsCallable<{ sessionId: string }, { ended: boolean }>(
    functions,
    'endImpersonation'
  );
  await callable({ sessionId });
}
